import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, SearchX, AlertTriangle, Loader2 } from "lucide-react";

export const inputCls =
  "w-full h-11 rounded-md border border-border bg-white px-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors";

export const labelCls = "block text-xs font-semibold uppercase tracking-[0.14em] text-primary mb-1.5";

export function SectionLabel({ children }) {
  return <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent mb-3">{children}</p>;
}

export function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex items-center flex-wrap gap-1 text-sm text-muted-foreground">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5 opacity-50" aria-hidden />}
          {it.to ? (
            <Link to={it.to} className="hover:text-primary transition-colors">{it.label}</Link>
          ) : (
            <span className="text-foreground font-medium">{it.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function EmptyState({ title = "Nothing found", message, action }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center text-center py-20 px-6">
      <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-5">
        <SearchX className="h-6 w-6 text-primary" aria-hidden />
      </div>
      <h3 className="font-display text-xl text-foreground mb-2">{title}</h3>
      {message && <p className="text-muted-foreground max-w-md">{message}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ErrorState({ message = "Unable to load content. Please try again.", onRetry }) {
  return (
    <div data-testid="error-state" className="flex flex-col items-center justify-center text-center py-20 px-6">
      <AlertTriangle className="h-8 w-8 text-destructive mb-4" aria-hidden />
      <p className="text-muted-foreground max-w-md">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-5 text-primary font-medium underline underline-offset-4">Retry</button>
      )}
    </div>
  );
}

export function Spinner() {
  return <Loader2 className="h-5 w-5 animate-spin" aria-hidden />;
}

export function CardSkeleton() {
  return (
    <div className="rounded-md border border-border overflow-hidden bg-white animate-pulse">
      <div className="h-56 bg-muted" />
      <div className="p-5 space-y-3">
        <div className="h-4 w-3/4 bg-muted rounded" />
        <div className="h-3 w-1/2 bg-muted rounded" />
        <div className="h-3 w-1/3 bg-muted rounded" />
      </div>
    </div>
  );
}

export function GridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => <CardSkeleton key={i} />)}
    </div>
  );
}
